import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ClientLink, Currency, DateTime, Table as DetailTable, ExportDropdown, Icon, RouteLink } from '~/components';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import CreditNoteFiltersBar from '~/components/filters/CreditNoteFiltersBar';
import CreditNoteFiltersGroup from '~/components/filters/CreditNoteFiltersGroup';
import CurrencyFilter from '~/components/filters/CurrencyFilter';
import FilterButton from '~/components/filters/FilterButton';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useNumberFormat, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import creditNoteStatuses from '~/lookups/credit-note-statuses';
import QBOIndicator from '~/routes/app/billing/credit-notes/components/QBOIndicator';
import SentIndicator from '~/routes/app/billing/credit-notes/components/SentIndicator';
import XeroIndicator from '~/routes/app/billing/credit-notes/components/XeroIndicator';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, intervalOptions } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const Byline = styled.small`
  display: block;
`;

export default function CreditNoteDetail({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();

  const [query, setQuery] = useState({
    report: null,
    status: 'loading',
    loading: { table: false },
  });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();

  const [params, setParams] = useState({
    period: null,
    creditNoteStatuses: [creditNoteStatuses.open],
    creditNoteSent: null,
    currency: workspace.currency,
    sort: new QuerySort('creditNote.issuedOn', 'desc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...reportsSearchParamsConfig.period, default: intervalOptions.all_dates },
        creditNoteStatuses: {
          ...searchParamsConfig.creditNoteStatuses,
          default: [creditNoteStatuses.draft, creditNoteStatuses.open],
        },
        creditNoteSent: searchParamsConfig.creditNoteSent,
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('creditNote.issuedOn', 'desc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [searchParamsConfig, reportsSearchParamsConfig, clientFilters, projectFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      creditNoteStatusId: params.creditNoteStatuses.map((v) => v.id),
      creditNoteSent: params.creditNoteSent ?? undefined,
      includeLines: true,
      currency: params.currency ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: { table: false },
    }));
  }, [api, workspace.id, urlSearchParams, report.id]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  if (!query.data) return null;
  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} data-testid="filters_button" />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />
        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}
        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
        <CreditNoteFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;
  const { workspace } = useWorkspace();

  const quantityFormat = useNumberFormat({ minimumFractionDigits: 0 });

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column width="12rem" name="creditNote.transactionNumber" onSort={onSort} sort={params.sort}>
            #
          </Table.Column>
          <Table.Column minWidth="16rem" name="client.name" onSort={onSort} sort={params.sort}>
            Client
          </Table.Column>
          <Table.Column width="8rem" align="right" name="creditNote.issuedOn" onSort={onSort} sort={params.sort}>
            Issued
          </Table.Column>
          <Table.Column width="6rem" name="statusId" onSort={onSort} sort={params.sort}>
            Status
          </Table.Column>
          <Table.Column width="10rem" align="right" name="convertedTax" onSort={onSort} sort={params.sort}>
            Tax
          </Table.Column>
          <Table.Column width="10rem" align="right" name="convertedBalance" onSort={onSort} sort={params.sort}>
            Remaining
          </Table.Column>
          <Table.Column width="10rem" align="right" name="convertedTotal" onSort={onSort} sort={params.sort}>
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.loading.table}>
          {report.records.map((creditNote) => {
            return (
              <React.Fragment key={creditNote.id}>
                <Table.GroupRow top="3rem">
                  <Table.Cell>
                    <RouteLink to={`/app/${workspace.key}/billing/credit-notes/${creditNote.id}`}>
                      {creditNote.number}
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <ClientLink client={creditNote.client} />
                  </Table.Cell>
                  <Table.Cell>
                    <DateTime value={creditNote.issuedOn} />
                    {creditNote.sentAt && <SentIndicator sentAt={creditNote.sentAt} />}
                  </Table.Cell>
                  <Table.Cell>
                    {creditNote.status.name}
                    {creditNote.qboCreditMemoId && (
                      <span>
                        <QBOIndicator />
                      </span>
                    )}
                    {creditNote.xeroCreditNoteId && (
                      <span>
                        <XeroIndicator xeroCreditNoteId={creditNote.xeroCreditNoteId} />
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <p>
                      <Currency value={creditNote.convertedTax} currency={report.currency} />
                      {creditNote.currency !== report.currency && (
                        <Byline>
                          <Currency value={creditNote.tax} currency={creditNote.currency} />
                        </Byline>
                      )}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p>
                      <Currency value={creditNote.convertedBalance} currency={report.currency} />
                      {creditNote.currency !== report.currency && (
                        <Byline>
                          <Currency value={creditNote.balance} currency={creditNote.currency} />
                        </Byline>
                      )}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p>
                      <Currency value={creditNote.convertedTotal} currency={report.currency} />
                      {creditNote.currency !== report.currency && (
                        <Byline>
                          <Currency value={creditNote.total} currency={creditNote.currency} />
                        </Byline>
                      )}
                    </p>
                  </Table.Cell>
                </Table.GroupRow>

                {creditNote.lines.length > 0 && (
                  <DetailTable style={{ fontSize: '.75rem' }}>
                    <DetailTable.Header style={{ background: colors.white }}>
                      <DetailTable.Column width="16rem">Item</DetailTable.Column>
                      <DetailTable.Column minWidth="20rem">Details</DetailTable.Column>
                      <DetailTable.Column align="center" width="5.5rem">
                        Taxable
                      </DetailTable.Column>
                      <DetailTable.Column align="right" width="7.5rem">
                        Quantity
                      </DetailTable.Column>
                      <DetailTable.Column align="right" width="7.5rem">
                        Rate
                      </DetailTable.Column>
                      <DetailTable.Column align="right" width="10rem">
                        Amount
                      </DetailTable.Column>
                    </DetailTable.Header>

                    <DetailTable.Body>
                      {creditNote.lines.map((line) => {
                        return (
                          <DetailTable.Row key={line.id}>
                            <DetailTable.Cell>{line.invoiceItem?.name}</DetailTable.Cell>
                            <DetailTable.Cell>{line.description}</DetailTable.Cell>
                            <DetailTable.Cell>{line.taxable && <Icon icon="check" />}</DetailTable.Cell>
                            <DetailTable.Cell>
                              {line.quantity ? quantityFormat.format(line.quantity) : ''}
                            </DetailTable.Cell>
                            <DetailTable.Cell>
                              <p>
                                <Currency
                                  value={line.convertedRate}
                                  currency={report.currency}
                                  maximumFractionDigits={7}
                                />
                                {line.convertedRate != null && creditNote.currency !== report.currency && (
                                  <Byline>
                                    <Currency
                                      value={line.rate}
                                      currency={creditNote.currency}
                                      maximumFractionDigits={7}
                                    />
                                  </Byline>
                                )}
                              </p>
                            </DetailTable.Cell>
                            <DetailTable.Cell>
                              <p>
                                <Currency value={line.convertedAmount} currency={report.currency} />
                                {line.convertedAmount != null && creditNote.currency !== report.currency && (
                                  <Byline>
                                    <Currency value={line.amount} currency={creditNote.currency} />
                                  </Byline>
                                )}
                              </p>
                            </DetailTable.Cell>
                          </DetailTable.Row>
                        );
                      })}
                    </DetailTable.Body>
                  </DetailTable>
                )}
              </React.Fragment>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell>
              <Currency value={report.totals.balance} currency={report.currency} />
            </Table.Cell>
            <Table.Cell>
              <Currency value={report.totals.total} currency={report.currency} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={report.records.length}
          label="Credit Note"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <CreditNoteFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
